import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { api, apiErr, API } from "@/lib/api";
import { Modal, Field, money } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Pencil, X, Upload, ShoppingBag, ChevronLeft, ChevronRight, ZoomIn, Share2 } from "lucide-react";

const MAX = 5;

function PhotoManager({ item, onChanged }) {
  const photos = item.photos || [];
  const add = async (files) => {
    const list = Array.from(files || []).slice(0, MAX - photos.length);
    for (const f of list) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.raw.post(`/store/items/${item.id}/photo`, fd); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); break; }
    }
    onChanged();
  };
  const remove = async (path) => {
    try { await api.post(`/store/items/${item.id}/photos/remove`, { path }); onChanged(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={p} className="relative" data-testid={`edit-item-photo-${i}`}>
            <img src={`${API}/files/${p}`} alt="" className="h-20 w-20 rounded object-cover border border-border bg-white" />
            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center">Principale</span>}
            <button type="button" onClick={() => remove(p)} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5" aria-label="Retirer la photo" data-testid={`edit-item-photo-remove-${i}`}><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
      {photos.length < MAX ? (
        <label className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-1.5 text-sm text-primary cursor-pointer hover:bg-muted" data-testid="edit-item-photo-add">
          <Upload className="h-4 w-4" />Ajouter des photos ({photos.length}/{MAX})
          <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="edit-item-photo-input" />
        </label>
      ) : <p className="text-xs text-muted-foreground">5 photos maximum atteint.</p>}
    </div>
  );
}

export function StoreItemEditButton({ item, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const start = () => { setF({ name: item.name, price: item.price, stock: item.stock, description: item.description || "" }); setOpen(true); };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Le nom de l'article est requis"); return; }
    try {
      await api.patch(`/store/items/${item.id}`, { name: f.name.trim(), price: Number(f.price) || 0, stock: parseInt(f.stock || 0, 10) || 0, active: item.active, description: f.description });
      toast.success("Article modifié"); setOpen(false); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={start} data-testid={`item-edit-${item.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Modifier</Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Modifier l'article" footer={<Button onClick={save} data-testid="edit-item-save">Enregistrer</Button>}>
        {f && (
          <div className="space-y-3">
            <Field label="Nom de l'article"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-item-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Prix ($)"><Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="edit-item-price" /></Field>
              <Field label="Stock"><Input type="number" min="0" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="edit-item-stock" /></Field>
            </div>
            <Field label="Description (visible par les parents)"><Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="edit-item-description" /></Field>
            <Field label="Photos (jusqu'à 5)"><PhotoManager item={item} onChanged={onSaved} /></Field>
          </div>
        )}
      </Modal>
    </>
  );
}

export function PhotoLightbox({ srcs, start = 0, alt, onClose }) {
  const [i, setI] = useState(start);
  const [touchX, setTouchX] = useState(null);
  const n = srcs.length;
  const go = (d) => setI((v) => (v + d + n) % n);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="photo-lightbox"
      onTouchStart={(e) => setTouchX(e.touches[0].clientX)}
      onTouchEnd={(e) => { if (touchX == null || n < 2) return; const dx = e.changedTouches[0].clientX - touchX; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); setTouchX(null); }}>
      <img src={srcs[i]} alt={alt} className="max-h-[90vh] max-w-[95vw] object-contain select-none" onClick={(e) => e.stopPropagation()} data-testid="photo-lightbox-img" />
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute top-3 right-3 h-11 w-11 rounded-full bg-white/15 text-white grid place-items-center hover:bg-white/25" data-testid="photo-lightbox-close"><X className="h-6 w-6" /></button>
      {n > 1 && <>
        <button type="button" aria-label="Photo précédente" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 h-11 w-11 rounded-full bg-white/15 text-white grid place-items-center hover:bg-white/25" data-testid="photo-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
        <button type="button" aria-label="Photo suivante" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 h-11 w-11 rounded-full bg-white/15 text-white grid place-items-center hover:bg-white/25" data-testid="photo-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
        <p className="absolute bottom-4 text-white/80 text-sm" data-testid="photo-lightbox-count">{i + 1} / {n}</p>
      </>}
    </div>,
    document.body
  );
}

export function ZoomableImg({ src, alt, className, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="relative block w-full cursor-zoom-in group" aria-label="Agrandir la photo" data-testid={testid ? `${testid}-zoom` : undefined}>
        <img src={src} alt={alt} loading="lazy" className={className} data-testid={testid} />
        <span className="absolute bottom-1 right-1 rounded-full bg-black/50 p-1 text-white opacity-80 group-hover:opacity-100"><ZoomIn className="h-3.5 w-3.5" /></span>
      </button>
      {open && <PhotoLightbox srcs={[src]} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

export function StoreItemGallery({ item }) {
  const photos = item.photos || [];
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  if (!photos.length) return <div className="w-full aspect-square bg-secondary grid place-items-center"><ShoppingBag className="h-8 w-8 text-muted-foreground" /></div>;
  const cur = photos[Math.min(idx, photos.length - 1)];
  return (
    <div>
      <button type="button" onClick={() => setZoom(true)} className="relative block w-full cursor-zoom-in group" aria-label="Agrandir la photo" data-testid={`custom-item-zoom-${item.id}`}>
        <img src={`${API}/files/${cur}`} alt={item.name} loading="lazy" className="w-full aspect-square object-cover bg-white" data-testid={`custom-item-photo-${item.id}`} />
        <span className="absolute bottom-2 right-2 rounded-full bg-black/50 p-1.5 text-white opacity-80 group-hover:opacity-100"><ZoomIn className="h-4 w-4" /></span>
      </button>
      {zoom && <PhotoLightbox srcs={photos.map((p) => `${API}/files/${p}`)} start={Math.min(idx, photos.length - 1)} alt={item.name} onClose={() => setZoom(false)} />}
      {photos.length > 1 && (
        <div className="flex gap-1 p-1 overflow-x-auto" data-testid={`custom-item-thumbs-${item.id}`}>
          {photos.map((p, i) => (
            <button key={p} type="button" onClick={() => setIdx(i)} className={`shrink-0 rounded border-2 ${i === idx ? "border-primary" : "border-transparent"}`} data-testid={`custom-item-thumb-${item.id}-${i}`}>
              <img src={`${API}/files/${p}`} alt="" className="h-10 w-10 object-cover rounded" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ShareWhatsApp({ item }) {
  const [busy, setBusy] = useState(false);
  const photo = (item.photos || [])[0];
  const photoUrl = photo ? `${API}/files/${photo}` : "";
  const text = [`Boutique ICDH : ${item.name}`, `Prix : ${money(item.price)}`, item.description || "", photoUrl ? `Photo : ${photoUrl}` : ""].filter(Boolean).join("\n");
  const share = async () => {
    setBusy(true);
    try {
      if (photo && navigator.canShare) {
        const blob = await (await fetch(photoUrl, { credentials: "include" })).blob();
        const file = new File([blob], `${item.name.replace(/[^\w-]+/g, "_")}.${(blob.type.split("/")[1] || "jpg")}`, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: text.replace(/\nPhoto : .*$/, "") });
          return;
        }
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    } catch (e) {
      if (e?.name !== "AbortError") window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="w-full h-auto py-1.5 whitespace-normal text-xs leading-tight border-emerald-500 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={share} data-testid={`custom-item-share-${item.id}`}>
      <Share2 className="h-4 w-4 mr-1 shrink-0" />{busy ? "…" : "Partager sur WhatsApp"}
    </Button>
  );
}

export const ItemPrice = ({ item }) => (
  <p className="font-heading font-extrabold text-2xl text-emerald-600" data-testid={`custom-item-price-${item.id}`}>{money(item.price)}</p>
);
